// API functions for bank status management
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

/**
 * Get all bank records for a building
 * @param {number} buildingId - Building ID
 * @returns {Promise<Array>} - List of bank records
 */
export const getBanksByBuilding = async (buildingId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank/building/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching banks:', error);
    throw error;
  }
};

/**
 * Get a single bank record by ID
 * @param {number} id - Bank record ID
 * @returns {Promise<Object>} - Bank record
 */
export const getBankById = async (id) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching bank:', error);
    throw error;
  }
};

/**
 * Create a new bank record
 * @param {Object} bankData - Bank data { bankName, year, month, moneyLeft, buildingID }
 * @returns {Promise<Object>} - Created bank record
 */
export const createBank = async (bankData) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(bankData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating bank:', error);
    throw error;
  }
};

/**
 * Update a bank record
 * @param {number} id - Bank record ID
 * @param {Object} bankData - Bank data to update
 * @returns {Promise<Object>} - Updated bank record
 */
export const updateBank = async (id, bankData) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(bankData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating bank:', error);
    throw error;
  }
};

/**
 * Delete a bank record
 * @param {number} id - Bank record ID
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteBank = async (id) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank/delete/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting bank:', error);
    throw error;
  }
};

/**
 * Get the latest bank record for a building
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object|null>} - Latest bank record or null
 */
export const getLatestBank = async (buildingId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Bank/latest/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching latest bank:', error);
    throw error;
  }
};

/**
 * Get the latest bank record for a building (External Dashboard - no auth required)
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object|null>} - Latest bank record or null
 */
export const getLatestBankExternal = async (buildingId) => {
  try {
    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const response = await fetch(`${API_URL}/api/Bank/external/latest/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
        // No Authorization header for external dashboard
      }
    });

    if (!response.ok) {
      // Return null instead of throwing for external dashboard
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching latest bank (external):', error);
    return null;
  }
};
